import cln from 'classnames';
import React, { useRef } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { Permission } from '../api/roles.dao';
import { getNavigationLabels, SEPARATOR } from '../constants/navigation-labels.constants';
import { SAVED_ROUTE_STORAGE_NAME } from '../constants/common.constants';
import { IEventDetailsDTO } from '../api/events.dao';
import { Events } from './_events/events';
import { Accounts } from './_accounts/accounts';
import { RootPages, Route } from './global.viewmodel';
import { connectGlobalVM, EventDetailsContext } from './global.context';
import { AuthGuard } from './_auth-guard';
import { ZoomPage } from './_zoom/zoom';
import './global.scss';
import './spacings.scss';
import { AlertsHost } from './global/modal/alerts-host/alerts-host';
import { Header } from './_header/header';
import { SubWindowsHost } from './global/sub-window/sub-windows-host';
import { JobsMonitorHost } from './_jobs-monitor-host/jobs-monitor-host';
import NavigationBar from './_events/_manage-event/shared/navigation-bar/navigation-bar';
import './layout.scss';
import { Operators } from './_operators/operators';
import { Users } from './_users/users';
import { Roles } from './_roles/roles';
import { Themes } from './_themes/themes';
import { RestrictedContent } from './_restricted-content/restricted-content';
import { UserGroups } from './_user-groups/user-groups';
import { ManageEventTracker } from './_event-tracker/manage-event-tracker';
import { AccountGroups } from './_account-groups/account-groups';
import { AccountAdmin } from './_account-admin/account-admin';
import { AccountUsers } from './_account-users/account-users';
import { Blueprints } from './_blueprints/blueprints';
import { EmailTemplates } from './_email-templates/email-templates';
import { EventHubs } from './_event-hubs/event-hubs';
import { ManageAttributes } from './_account-admin/_attributes/manage-attributes';
import { OeData } from './_oe-data/oe-data';

export interface ILayoutPresentationProps {
    startListeningRouter: (customRoute?: Route) => void;
    setIntl: (intl: IntlShape) => void;
    selectedRootPage: RootPages;
    isFullScreen: boolean;
    intl?: IntlShape;
}

export const RouteListener = ({ startListeningRouter, routerIsListenedRef }: {
    startListeningRouter: (customRoute?: Route) => void;
    routerIsListenedRef: React.MutableRefObject<boolean>;
}) => {
    React.useEffect(() => {
        let completeRoute: Route;
        const savedRoute = localStorage.getItem(SAVED_ROUTE_STORAGE_NAME);
        if (savedRoute) {
            try {
                const { path, params } = JSON.parse(savedRoute);
                let currentPath = path;

                Object.keys(params).map(key => {
                    currentPath = currentPath.replace(params[key], `:${key}`);
                });

                completeRoute = {
                    path: currentPath,
                    params: params
                };
            } finally {
                localStorage.removeItem(SAVED_ROUTE_STORAGE_NAME);
            }
        }
        startListeningRouter(completeRoute);
        routerIsListenedRef.current = true;
    }, []);

    return null;
};

export const LayoutPresentation = (props: ILayoutPresentationProps) => {
    const scroller = React.useRef<HTMLDivElement | null>(null);
    const { selectedRootPage, intl, startListeningRouter, setIntl, isFullScreen } = props;
    const [eventDetails, setEventDetails] = React.useState(null);
    const [animate, setAnimate] = React.useState(false);
    /* routerIsListenedRef is used to prevent rendering the RestrictedContent component before the router is started be listened to*/
    const routerIsListenedRef = useRef(false);

    const labels = getNavigationLabels(intl);
    const topHeaderHeight = 92;
    const eventDetailsHandler = (event: IEventDetailsDTO) => {
        setEventDetails(event);
    };

    let pageTitle = '';
    let rootPage: React.ReactElement;
    switch (selectedRootPage) {
        case RootPages.Accounts:
            rootPage = <RestrictedContent content={<Accounts />} permission={Permission.ACCOUNTS_LIST}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ACCOUNTS_LIST}`;
            break;
        case RootPages.AccountGroups:
            rootPage = <RestrictedContent content={<AccountGroups />} permission={Permission.ACCOUNT_GROUPS_LIST}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ACCOUNT_GROUP_LIST}`;
            break;
        case RootPages.Events:
            rootPage = <Events />;
            break;
        case RootPages.EventHubs:
            rootPage = <RestrictedContent content={<EventHubs />} permission={Permission.EVENT_HUBS_TAB} />;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.EVENT_HUBS}`;
            break;
        case RootPages.Blueprints:
            rootPage = <RestrictedContent content={<Blueprints />} permission={Permission.BLUEPRINTS_TAB}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.BLUEPRINTS}`;
            break;
        case RootPages.AccountUsers:
            rootPage = <RestrictedContent content={<AccountUsers />} permission={Permission.ACCOUNT_USERS_TAB}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ACCOUNT_USERS_LIST}`;
            break;
        case RootPages.UserGroups:
            rootPage = <RestrictedContent content={<UserGroups />} permission={Permission.USER_GROUP_ADMIN}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.USER_GROUPS_LIST}`;
            break;
        case RootPages.AccountAdmin:
            rootPage = <RestrictedContent content={<AccountAdmin />} permission={Permission.ACCOUNT_ADMIN_TAB}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ACCOUNT_ADMIN}`;
            break;
        case RootPages.ManageAttributes:
            rootPage = <RestrictedContent content={<ManageAttributes />} permission={Permission.VIEW_EVENT_ATTRIBUTES}/>;
            break;
        case RootPages.EmailTemplates:
            rootPage = <RestrictedContent content={<EmailTemplates />} permission={Permission.EMAIL_TEMPLATES_TAB}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.EMAIL_TEMPLATES}`;
            break;
        case RootPages.Zoom:
            rootPage = <RestrictedContent content={<ZoomPage />} permission={Permission.RESOURCES}/>;
            break;
        case RootPages.Operators:
            rootPage = <RestrictedContent content={<Operators />} permission={Permission.OPERATORS}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.OPERATORS_LIST}`;
            break;
        case RootPages.Users:
            rootPage = <RestrictedContent content={<Users />} permission={Permission.USER_TAB}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.USERS_LIST}`;
            break;
        case RootPages.Roles:
            rootPage = <RestrictedContent content={<Roles />} permission={Permission.ROLES}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ROLES_LIST}`;
            break;
        case RootPages.Themes:
            rootPage = <RestrictedContent content={<Themes />} permission={Permission.THEMES}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.THEMES_LIST}`;
            break;
        case RootPages.EventTracker:
            rootPage = <RestrictedContent content={<ManageEventTracker />} permission={Permission.EVENT_TRACKER}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.EVENT_TRACKER_LIST}`;
            break;
        case RootPages.OeData:
            rootPage = <RestrictedContent content={<OeData />} permission={Permission.VIEW_OE_DATA_ORGANIZATIONS}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.OE_DATA}`;
            break;
        case RootPages.Restricted:
            rootPage = <RestrictedContent />;
            break;
        default:
            rootPage = <RestrictedContent content={<Accounts />} permission={Permission.ACCOUNTS_LIST}/>;
            pageTitle = `${labels.ROOT} ${SEPARATOR} ${labels.ACCOUNTS_LIST}`;
            break;
    }

    function updateScrollPosition() {
        if (scroller && scroller.current) {
            const pageYOffset = Math.abs(scroller.current.getBoundingClientRect().y);
            const offsetHeight = scroller.current.offsetHeight;
            const scrollHeight = scroller.current.scrollHeight;
            const viewWindowHeight = offsetHeight + (2 * topHeaderHeight);
            if (scrollHeight > viewWindowHeight && pageYOffset > topHeaderHeight) {
                setAnimate(true);
            } else {
                setAnimate(false);
            }
        }
    }
    React.useEffect(() => {
        window.addEventListener('scroll', updateScrollPosition, false);
        return () => window.removeEventListener('scroll', updateScrollPosition, false);
    });

    React.useEffect(() => {
        setIntl(intl);
    }, []);

    React.useEffect(() => {
        if (pageTitle) {
            document.title = pageTitle;
        }
    }, [pageTitle]);

    return (
        <div className={cln('oe-connect', { 'oe-connect--full-screen': isFullScreen })} ref={scroller}>
            <AuthGuard>
                <RouteListener startListeningRouter={startListeningRouter} routerIsListenedRef={routerIsListenedRef} />
                <SubWindowsHost>
                    <EventDetailsContext.Provider value={{ eventDetails: eventDetails, eventDetailsHandler: eventDetailsHandler }}>
                        {routerIsListenedRef.current && <>
                            {
                                !isFullScreen && <div className={cln('oe-connect__header', {
                                    'oe-connect__header--animate': animate
                                })}>
                                    <Header />
                                    <NavigationBar />
                                </div>
                            }
                            <div className={cln('oe-connect__root-page', {
                                'oe-connect__root-page--animate': animate && !isFullScreen,
                                'oe-connect__root-page--full-screen': isFullScreen,
                            })}>
                                {rootPage}
                            </div>
                        </>}
                        <JobsMonitorHost/>
                        <AlertsHost/>
                    </EventDetailsContext.Provider>
                </SubWindowsHost>
            </AuthGuard>
        </div>
    );
};

const Layout = injectIntl(connectGlobalVM<ILayoutPresentationProps>(LayoutPresentation, vm => {
    return {
        setIntl: vm.setIntl,
        startListeningRouter: vm.startListeningRouter,
        selectedRootPage: vm.getRootPage(),
        isFullScreen: vm.isFullScreen,
    };
}));

export { Layout };
